import { useMemo } from 'react';
import { useWorkflowStore } from '@/stores/workflowStore';

export interface AvailableVariable {
  name: string;
  type: string;
}

/**
 * Extract variable definitions from InitializeVariable actions in the workflow.
 * Recursively scans nested scopes (If/Switch/Foreach/Until/Scope).
 * Used by Set/Append/Increment/Decrement Variable actions to populate
 * the variable name dropdown.
 */
export function useAvailableVariables(): AvailableVariable[] {
  const actions = useWorkflowStore((s) => s.workflow?.definition?.actions);

  return useMemo(() => {
    if (!actions) return [];

    const variables: AvailableVariable[] = [];
    const seen = new Set<string>();

    function scanActions(actionMap: Record<string, unknown>) {
      for (const action of Object.values(actionMap)) {
        const record = action as Record<string, unknown>;
        if (!record || typeof record !== 'object') continue;

        // Extract variables from InitializeVariable actions
        if (record.type === 'InitializeVariable') {
          const inputs = record.inputs as Record<string, unknown> | undefined;
          if (inputs) {
            const vars = inputs.variables;
            if (Array.isArray(vars)) {
              for (const v of vars) {
                const entry = v as Record<string, unknown>;
                const name = entry?.name;
                const type = entry?.type;
                if (typeof name === 'string' && name && !seen.has(name)) {
                  seen.add(name);
                  variables.push({ name, type: typeof type === 'string' ? type : 'String' });
                }
              }
            }
          }
        }

        // Recurse into nested scopes
        // If/Scope: record.actions
        if (record.actions && typeof record.actions === 'object') {
          scanActions(record.actions as Record<string, unknown>);
        }
        // If: record.else?.actions
        const elseBlock = record.else as Record<string, unknown> | undefined;
        if (elseBlock?.actions && typeof elseBlock.actions === 'object') {
          scanActions(elseBlock.actions as Record<string, unknown>);
        }
        // Switch: record.cases.{caseName}.actions + record.default?.actions
        if (record.cases && typeof record.cases === 'object') {
          for (const caseVal of Object.values(record.cases as Record<string, unknown>)) {
            const caseRecord = caseVal as Record<string, unknown>;
            if (caseRecord?.actions && typeof caseRecord.actions === 'object') {
              scanActions(caseRecord.actions as Record<string, unknown>);
            }
          }
        }
        const defaultBlock = record.default as Record<string, unknown> | undefined;
        if (defaultBlock?.actions && typeof defaultBlock.actions === 'object') {
          scanActions(defaultBlock.actions as Record<string, unknown>);
        }
      }
    }

    scanActions(actions as Record<string, unknown>);
    variables.sort((a, b) => a.name.localeCompare(b.name));
    return variables;
  }, [actions]);
}
